import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  signInOperation,
  signUpOperation,
  signOutOperation,
  refreshTokenOperation,
  getUserOperation,
  updateUserOperation,
  resendVerificationEmailOperation,
  forgotPasswordOperation,
  resetPasswordOperation,
  changePasswordOperation,
  bindTelegramOperation,
} from './auth-operations';
import { AuthState } from './AuthTypes';
import { clearCompanyDataAttribute } from '@utils/companyTheme';

export const initialState: AuthState = {
  accessToken: null,
  tokenType: null,
  user: null,
  isLoggedIn: false,
  error: null,
  loading: false,
  initialized: false, // <-- добавляем
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserActual: (s, { payload }: PayloadAction<{ is_actual: boolean }>) => {
      if (s.user) {
        s.user.is_actual = payload.is_actual;
        // RenewTest / UI зависят от даты последнего теста — без неё после
        // прохождения остаётся стейт «не проходил» до релоада.
        if (payload.is_actual) {
          s.user.last_survey_completed_at = new Date().toISOString();
        }
      }
    },
    clearAuthError: s => {
      s.error = null;
    },
  },

  extraReducers: builder => {
    /* ───────── токены ───────── */
    builder.addCase(signInOperation.pending, s => {
      // Для login используем локальный isSubmitting в форме, без глобального loading.
      s.error = null;
    });

    builder.addCase(signInOperation.fulfilled, (s, { payload }) => {
      s.accessToken = payload.access_token;
      s.tokenType = payload.token_type;
      s.isLoggedIn = true;
      s.error = null;
    });

    builder.addCase(signUpOperation.fulfilled, (s, { payload }) => {
      s.accessToken = payload.access_token;
      s.tokenType = payload.token_type;
      s.isLoggedIn = true;
      s.error = null;
    });

    builder.addCase(refreshTokenOperation.fulfilled, (s, { payload }) => {
      s.accessToken = payload.access_token;
      s.tokenType = payload.token_type;
      s.isLoggedIn = true;
      s.error = null;
    });

    /* ───────── профиль ───────── */
    builder.addCase(getUserOperation.fulfilled, (s, { payload }) => {
      s.user = payload;
      s.isLoggedIn = true;
      s.error = null;
      s.initialized = true;
    });

    builder.addCase(getUserOperation.rejected, s => {
      s.initialized = true; // <-- тоже завершаем инициализацию
    });

    builder.addCase(getUserOperation.pending, s => {
      s.initialized = false; // <-- начинаем заново
    });

    builder.addCase(updateUserOperation.fulfilled, (s, { payload }) => {
      s.user = payload;
      s.error = null;
    });

    /* ───────── sign-out ───────── */
    builder.addCase(signOutOperation.fulfilled, () => {
      // Очищаем data-company атрибут при выходе
      clearCompanyDataAttribute();
      return initialState;
    });

    /* ───────── refresh token failed ───────── */
    builder.addCase(refreshTokenOperation.rejected, () => {
      // Очищаем data-company атрибут при неудачном обновлении токена
      clearCompanyDataAttribute();
      return initialState;
    });

    /* ───────── повторная отправка подтверждения почты  ───────── */
    builder
      .addCase(resendVerificationEmailOperation.pending, s => {
        s.loading = true;
        s.error = null;
      })
      .addCase(resendVerificationEmailOperation.fulfilled, s => {
        s.loading = false;
        s.error = null;
      })
      .addCase(resendVerificationEmailOperation.rejected, (s, { payload, error }) => {
        s.loading = false;
        s.error = (payload as any)?.message || error.message || null;
      });

    /* ───────── запрос на письмо с восстановлением пароля  ───────── */
    builder
      .addCase(forgotPasswordOperation.pending, s => {
        s.loading = true;
        s.error = null;
      })
      .addCase(forgotPasswordOperation.fulfilled, s => {
        s.loading = false;
        s.error = null;
      })
      .addCase(forgotPasswordOperation.rejected, (s, { payload, error }) => {
        s.loading = false;
        s.error = (payload as any)?.message || error.message || null;
      });

    /* ───────── reset password  ───────── */
    builder
      .addCase(resetPasswordOperation.pending, s => {
        s.loading = true;
        s.error = null;
      })
      .addCase(resetPasswordOperation.fulfilled, s => {
        s.loading = false;
        s.error = null;
      })
      .addCase(resetPasswordOperation.rejected, (s, { payload, error }) => {
        s.loading = false;
        s.error = (payload as any)?.message || error.message || null;
      });

    builder
      .addCase(changePasswordOperation.pending, s => {
        s.loading = true;
        s.error = null;
      })
      .addCase(changePasswordOperation.fulfilled, s => {
        s.loading = false;
        s.error = null;
      })
      .addCase(changePasswordOperation.rejected, (s, { payload, error }) => {
        s.loading = false;
        s.error = (payload as any)?.message || error.message || null;
      });

    /* ───────── telegram  ───────── */
    builder.addCase(bindTelegramOperation.pending, s => {
      s.loading = true;
      s.error = null;
    });
    builder.addCase(bindTelegramOperation.fulfilled, (s, { payload }) => {
      s.user = payload;
      s.loading = false;
      s.error = null;
    });
    builder.addCase(bindTelegramOperation.rejected, (s, { payload, error }) => {
      s.loading = false;
      s.error = (payload as any)?.message || error.message || null;
    });

    /* ───────── ошибки / loading ───────── */
    builder.addMatcher(
      isAnyOf(signUpOperation.pending, signOutOperation.pending, updateUserOperation.pending),
      s => {
        s.loading = true;
        s.error = null;
      },
    );

    builder.addMatcher(
      isAnyOf(
        signInOperation.rejected,
        signUpOperation.rejected,
        signOutOperation.rejected,
        updateUserOperation.rejected,
      ),
      (s, { payload, error }) => {
        s.loading = false;
        s.error = (payload as any)?.message || error.message || null;
      },
    );

    builder.addMatcher(
      isAnyOf(signInOperation.fulfilled, signUpOperation.fulfilled, signOutOperation.fulfilled, updateUserOperation.fulfilled),
      s => {
        s.loading = false;
      },
    );
  },
});

export const { setUserActual, clearAuthError } = authSlice.actions;
export default authSlice.reducer;
